/**
 * Copyright (c) 2024 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useState, useEffect } from 'react';
import { mixins, colors } from '@blend/consumer-components';
import { css, StyleSheet } from 'aphrodite';
import { useVideoContext } from '../../../../contexts/videoProvider/videoContext';
import { mediaBreakpoints, mediaOrientations } from '../../../../common/responsive';

// The line height of the caption text is crucial so that the overflow captions does not get clipped
// and they appear like they are scrolling up
const CAPTION_LINE_HEIGHT = 28;
const CAPTION_CONTAINER_HEIGHT_DESKTOP = CAPTION_LINE_HEIGHT * 3;
const CAPTION_CONTAINER_HEIGHT_MOBILE = CAPTION_LINE_HEIGHT * 2;
const CAPTION_CONTAINER_HEIGHT_ON_TAB_VIEW_PORTRAIT = CAPTION_LINE_HEIGHT * 10;
const CAPTION_CONTAINER_HEIGHT_ON_TAB_VIEW_LANDSCAPE = CAPTION_LINE_HEIGHT * 4;

const styles = StyleSheet.create({
  closedCaptionsWrapper: {
    position: 'relative',
    minHeight: CAPTION_CONTAINER_HEIGHT_DESKTOP,
    width: '50%',
    overflow: 'hidden',
    marginTop: 8,
    [mediaBreakpoints.mobile]: {
      minHeight: CAPTION_CONTAINER_HEIGHT_MOBILE,
      width: '100%',
    },
  },
  closedCaptionsContainer: {
    width: '100%',
    minHeight: CAPTION_CONTAINER_HEIGHT_DESKTOP,
    // Positioning it absolute so that the new captions are added at the bottom and the overflow hides older captions
    position: 'absolute',
    bottom: 0,
    color: 'white',
    lineHeight: '28px',

    [mediaBreakpoints.mobile]: {
      minHeight: CAPTION_CONTAINER_HEIGHT_MOBILE,
    },
  },
  caption: {
    fontSize: 16,
  },
  userNameLabel: {
    backgroundColor: colors.catalyst,
    borderRadius: 4,
    ...mixins.padding(2, 4),
    fontWeight: 500,
    lineHeight: `${CAPTION_LINE_HEIGHT - 4}px`, // Subtracting 4px to account for padding

    [mediaBreakpoints.mobile]: {
      // hide the name of user for mobile landscape view
      [mediaOrientations.landscape]: {
        display: 'none',
      },
    },
  },
  userNameLabelTabView: {
    [mediaBreakpoints.mobile]: {
      // enable the name again for Tab view
      [mediaOrientations.landscape]: {
        display: 'block',
      },
    },
  },

  // Below styles are for the Closed Captions in the Participants Tab View
  tabViewClosedCaptionsWrapper: {
    [mediaBreakpoints.mobile]: {
      [mediaOrientations.portrait]: {
        minHeight: CAPTION_CONTAINER_HEIGHT_ON_TAB_VIEW_PORTRAIT,
        ...mixins.margin(20, 0, 0),
      },

      [mediaOrientations.landscape]: {
        minHeight: CAPTION_CONTAINER_HEIGHT_ON_TAB_VIEW_LANDSCAPE,
        ...mixins.margin(8, 0, 0),
      },
    },
  },
  tabViewClosedCaptionsContainer: {
    [mediaBreakpoints.mobile]: {
      ...mixins.padding(0, 15, 0),
      [mediaOrientations.portrait]: {
        minHeight: CAPTION_CONTAINER_HEIGHT_ON_TAB_VIEW_PORTRAIT,
      },

      [mediaOrientations.landscape]: {
        minHeight: CAPTION_CONTAINER_HEIGHT_ON_TAB_VIEW_LANDSCAPE,
      },
    },
  },
  tabViewCaption: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'baseline',
    [mediaOrientations.portrait]: {
      ':not(:last-child)': {
        ...mixins.margin(0, 0, CAPTION_LINE_HEIGHT),
      },
    },
  },
});

interface Captions {
  [userId: string]: {
    user: string;
    text: string;
    timestamp: number;
  };
}

const CAPTION_EXPIRY_TIME = 6000;

export const ClosedCaptions: React.FC<{ isParticipantsTabView?: boolean }> = ({ isParticipantsTabView }) => {
  const { videoProvider } = useVideoContext();
  const [captions, setCaptions] = useState<Captions>({});

  useEffect(() => {
    const onClosedCaptioningMessageCallback = (payload: VideoProvider.ZoomCaptionPayload) => {
      setCaptions(prevCaptions => {
        const currentTime = new Date().getTime();
        const filteredCaptions = Object.keys(prevCaptions).reduce((result, userId) => {
          if (prevCaptions[userId].timestamp < currentTime - CAPTION_EXPIRY_TIME) {
            // Remove captions older than 6 seconds
            delete result[userId];
          }

          return result;
        }, prevCaptions);

        return {
          ...filteredCaptions,
          ...{
            [payload.userId]: {
              user: payload.displayName,
              text: payload.text,
              timestamp: new Date().getTime(),
            },
          },
        };
      });
    };

    videoProvider.onClosedCaptioningMessage(onClosedCaptioningMessageCallback);

    return () => {
      videoProvider.offloadClosedCaptioningMessage(onClosedCaptioningMessageCallback);
    };
  }, []);

  return (
    <div
      data-testid="closed-captions-wrapper"
      className={css(styles.closedCaptionsWrapper, isParticipantsTabView && styles.tabViewClosedCaptionsWrapper)}
    >
      <div
        data-testid="closed-captions-container"
        className={css(styles.closedCaptionsContainer, isParticipantsTabView && styles.tabViewClosedCaptionsContainer)}
      >
        {Object.values(captions)
          .sort((a, b) => a.timestamp - b.timestamp)
          .map(caption => {
            return (
              <div
                key={caption.timestamp}
                data-testid="caption"
                className={css(styles.caption, isParticipantsTabView && styles.tabViewCaption)}
              >
                <span
                  data-testid="caption-username"
                  className={css(styles.userNameLabel, isParticipantsTabView && styles.userNameLabelTabView)}
                >
                  {caption.user}
                </span>{' '}
                <span data-testid="caption-text">{caption.text}</span>
              </div>
            );
          })}
      </div>
    </div>
  );
};
